import { useMutation } from 'react-query';
import { Typography } from '@mui/material';
import { addMinutes, subHours } from 'date-fns';
import { useAxios } from 'hooks/useAxios';
import { useSnackbar } from 'notistack';
import { DEVICE_TYPE_IDS } from 'utils/constants';
import { keysToSnakeCase } from 'utils/formatters';
import * as yup from 'yup';
import { EventsForm } from './EventsForm';
import { TimeseriesForm } from './TimeseriesForm';

export type EventFormValues = {
  unitId: string;
  severity: string[];
  type: string[];
  source: string;
  message: string;
  code: string;
  period: string;
  from: string;
  to: string;
  duration: string;
};

export type TimeseriesFormValues = {
  unitId: string;
  from: string;
  to: string;
  duration: string;
  deviceType: number;
  hasSecondInverter: boolean;
};

const TEST_DATA_TYPES = {
  EVENTS: 'events',
  TIMESERIES: 'timeseries data',
  STATE: 'timeseries state data',
  INPUT: 'timeseries input data',
};

export const TIMESERIES_DEVICE_TYPE_OPTIONS = [
  { label: 'Battery', value: DEVICE_TYPE_IDS.BATTERY },
  { label: 'Energy meter', value: DEVICE_TYPE_IDS.ENERGY_METER },
];
export const SEVERITY_OPTIONS = ['error', 'warning', 'info'];
export const TYPE_OPTIONS = ['notification', 'command response'];
export const SOURCE_OPTIONS = ['greenerwatch', 'greenerhmi', 'telegram'];

export const Component = () => {
  const { post } = useAxios();
  const { enqueueSnackbar } = useSnackbar();

  const eventsValidationSchema = yup.object().shape({
    unitId: yup.string().required(),
    severity: yup.array().of(yup.string().oneOf(SEVERITY_OPTIONS)).min(1).required(),
    type: yup.array().of(yup.string().oneOf(TYPE_OPTIONS)).min(1).required(),
    source: yup.string().required(),
    message: yup.string(),
    code: yup.string().required(),
    period: yup.string().required(),
    from: yup.string().required(),
    to: yup.string().required(),
    duration: yup.string().required(),
  });

  const timeseriesValidationSchema = yup.object().shape({
    unitId: yup.string().required(),
    from: yup.string().required(),
    to: yup.string().required(),
    duration: yup.string().required(),
    deviceType: yup.number().required(),
    hasSecondInverter: yup.boolean().required(),
  });

  const defaultEventConfigValues = {
    unitId: '',
    severity: ['error'],
    message: 'This is a test event.',
    type: ['notification'],
    source: SOURCE_OPTIONS[0],
    code: '123',
    period: '30S',
    duration: '5M',
    from: new Date().toISOString(),
    to: addMinutes(new Date(), 2).toISOString(),
  };

  const defaultTimeseriesConfigValues = {
    unitId: '',
    duration: '1H',
    to: new Date().toISOString(),
    from: subHours(new Date(), 3).toISOString(),
    deviceType: DEVICE_TYPE_IDS.BATTERY,
    hasSecondInverter: false,
  };

  const createMutation = ({ endpoint, dataType }: { endpoint: string; dataType?: string }) =>
    useMutation(
      (payload: Record<string, unknown>) => post(endpoint, keysToSnakeCase(payload) as Record<string, unknown>),
      {
        onSuccess: () => {
          enqueueSnackbar(`Successfully generated test ${dataType}`, { variant: 'success' });
        },
        onError: () => enqueueSnackbar('Error', { variant: 'error' }),
      }
    );

  const singleEventMutation = createMutation({
    endpoint: '/test-data/generate-event/',
    dataType: TEST_DATA_TYPES.EVENTS,
  });
  const multipleEventsMutation = createMutation({
    endpoint: '/test-data/batch-generate-events/',
    dataType: TEST_DATA_TYPES.EVENTS,
  });
  const liveEventsMutation = createMutation({
    endpoint: '/test-data/generate-live-events/',
    dataType: TEST_DATA_TYPES.EVENTS,
  });
  const batchTimeseriesMutation = createMutation({
    endpoint: '/test-data/generate-batch-timeseries/',
    dataType: TEST_DATA_TYPES.TIMESERIES,
  });
  const liveTimeseriesMutation = createMutation({
    endpoint: '/test-data/generate-live-timeseries/',
    dataType: TEST_DATA_TYPES.TIMESERIES,
  });
  const batchStateMutation = createMutation({
    endpoint: '/test-data/generate-batch-state/',
    dataType: TEST_DATA_TYPES.STATE,
  });
  const liveStateMutation = createMutation({
    endpoint: '/test-data/generate-live-state/',
    dataType: TEST_DATA_TYPES.STATE,
  });
  const batchInputMutation = createMutation({
    endpoint: '/test-data/generate-batch-input/',
    dataType: TEST_DATA_TYPES.INPUT,
  });
  const liveInputMutation = createMutation({
    endpoint: '/test-data/generate-live-input/',
    dataType: TEST_DATA_TYPES.INPUT,
  });

  const createEventConfigsPayload = (formValues: EventFormValues) => ({
    ...formValues,
    unitId: formValues.unitId
      ?.split(',')
      .map((value: string) => value.trim())
      .filter(Boolean),
    code: formValues.code
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map(Number),
    message: [formValues.message],
    source: formValues.source ? [formValues.source] : undefined,
    severity: formValues.severity?.map((value) => value.toUpperCase()),
  });

  const createTimeseriesConfigPayload = (formValues: TimeseriesFormValues) => ({
    unitId: formValues.unitId,
    deviceType: formValues.deviceType,
  });

  const generateSingleEvent = (data: EventFormValues) => {
    const payload = createEventConfigsPayload(data);
    singleEventMutation.mutate(payload);
  };

  const generateMultipleEvents = (data: EventFormValues) => {
    const eventConfigs = createEventConfigsPayload(data);
    const payload = {
      eventDataOptions: eventConfigs,
      tsFrom: data.from,
      tsTo: data.to,
      period: `PT${data.period}`,
    };
    multipleEventsMutation.mutate(payload);
  };

  const generateLiveEvents = (data: EventFormValues) => {
    const eventConfigs = createEventConfigsPayload(data);
    const payload = {
      eventDataOptions: eventConfigs,
      period: `PT${data.period}`,
      duration: `PT${data.duration}`,
    };
    liveEventsMutation.mutate(payload);
  };

  const generateBatchTimeseries = (data: TimeseriesFormValues) => {
    const timeseriesConfigs = createTimeseriesConfigPayload(data);
    batchTimeseriesMutation.mutate({
      timeseriesDataOptions: timeseriesConfigs,
      tsFrom: data.from,
      tsTo: data.to,
    });
    if (data.deviceType === DEVICE_TYPE_IDS.BATTERY) {
      batchStateMutation.mutate({
        timeseriesDataOptions: timeseriesConfigs,
        tsFrom: data.from,
        tsTo: data.to,
      });
      if (data.hasSecondInverter) {
        batchInputMutation.mutate({
          timeseriesDataOptions: timeseriesConfigs,
          tsFrom: data.from,
          tsTo: data.to,
        });
      }
    }
  };

  const generateLiveTimeseries = (data: TimeseriesFormValues) => {
    const timeseriesConfigs = createTimeseriesConfigPayload(data);
    liveTimeseriesMutation.mutate({
      timeseriesDataOptions: timeseriesConfigs,
      duration: `PT${data.duration}`,
    });
    if (data.deviceType === DEVICE_TYPE_IDS.BATTERY) {
      liveStateMutation.mutate({
        timeseriesDataOptions: timeseriesConfigs,
        duration: `PT${data.duration}`,
      });
      if (data.hasSecondInverter) {
        liveInputMutation.mutate({
          timeseriesDataOptions: timeseriesConfigs,
          duration: `PT${data.duration}`,
        });
      }
    }
  };

  return (
    <>
      <Typography variant="h1">Generate test data</Typography>
      <Typography variant="h3">Events</Typography>
      <EventsForm
        defaultValues={defaultEventConfigValues}
        validationSchema={eventsValidationSchema}
        generateSingleEvent={generateSingleEvent}
        generateMultipleEvents={generateMultipleEvents}
        generateLiveEvents={generateLiveEvents}
      />
      <Typography variant="h3">Timeseries</Typography>
      <TimeseriesForm
        defaultValues={defaultTimeseriesConfigValues}
        validationSchema={timeseriesValidationSchema}
        generateBatchTimeseries={generateBatchTimeseries}
        generateLiveTimeseries={generateLiveTimeseries}
      />
    </>
  );
};

Component.displayName = 'TestDataGenerator';
